import { defineMessages } from 'react-intl';

import type { SavedSearch } from '~/features/search/types/search';
import type { EntityQuery } from '~/types/OpenSearch';

import { showToast } from '~/eds/src/components/Toast/showToast';
import { SaveSearchPermissionType } from '~/enums/SaveSearchPermissionType';
import {
  RESERVED_SCOPE_NAME,
  type HomeSettings,
} from '~/features/home/utils/homeSettings';
import { intlService } from '~/i18n/IntlService';
import { ServiceRequestConfig } from '~/services/createService';

import { getSavedSearches } from '../searchV3/getSavedSearches';
import { postSavedSearch } from '../searchV3/postSavedSearch';
import { putSavedSearch } from '../searchV3/putSavedSearch';
import { updateHomeSettings } from '../userPreferences';

const SCOPE_LOOKUP_PAGE_SIZE = 100;

const saveSuccessToast = {
  id: 'useHomeSettings.saveSuccess',
  defaultMessage: 'Home settings saved.',
  description: 'Success toast shown after saving Home settings',
};

const messages = defineMessages({
  savedSearchError: {
    id: 'Home.Scope.savedSearch.error',
    defaultMessage: 'We could not update your Home scope.',
    description:
      'Home scope: toast shown when the reserved saved search cannot be written',
  },
});

export type ApplyHomeScopeRequest = {
  settings: HomeSettings;
  description: string;
  query: EntityQuery[];
};

let queue: Promise<unknown> = Promise.resolve();

export function serializeScopeWrite<T>(run: () => Promise<T>) {
  const next = queue.then(run, run);
  queue = next.catch(() => {});
  return next;
}

export async function writeHomeScopePreference(
  homeSettings: HomeSettings,
  options: ServiceRequestConfig = {},
) {
  await updateHomeSettings(
    { homeSettings },
    {
      ...options,
      showSuccessToast:
        options.showSuccessToast ??
        intlService.getIntl().formatMessage(saveSuccessToast),
      showErrorToast: options.showErrorToast ?? true,
    },
  );
}

const testIsReservedScopeSearch = (search: SavedSearch) =>
  search.name === RESERVED_SCOPE_NAME &&
  search.visibility === SaveSearchPermissionType.Private &&
  // The list merges in v2 searches, whose ids the v3 PUT would not resolve.
  search.version === 3;

async function findReservedScopeSearchId() {
  try {
    const listPage = (page: number) =>
      getSavedSearches({
        page,
        pageSize: SCOPE_LOOKUP_PAGE_SIZE,
        types: ['mine'],
      });

    const { results, meta } = await listPage(1);
    const found = results.find(testIsReservedScopeSearch);
    if (found) {
      return found.id;
    }

    // The list is newest-first and the reserved search is created once and PUT
    // forever after, so it sinks off page 1 as the user saves more.
    const pageCount = Math.ceil(meta.total / SCOPE_LOOKUP_PAGE_SIZE);
    for (let page = 2; page <= pageCount; page += 1) {
      const next = await listPage(page);
      const match = next.results.find(testIsReservedScopeSearch);
      if (match) {
        return match.id;
      }
    }
    return null;
  } catch {
    return null;
  }
}

function toPreferenceId(id: SavedSearch['id']) {
  const numeric = typeof id === 'number' ? id : Number(id);
  return Number.isFinite(numeric) ? numeric : null;
}

export function applyHomeScope(
  request: ApplyHomeScopeRequest,
  options: ServiceRequestConfig = {},
) {
  return serializeScopeWrite(async () => {
    const { settings, description, query } = request;
    const payload = {
      name: RESERVED_SCOPE_NAME,
      query,
      filters: [],
      resultTable: [],
      visibility: SaveSearchPermissionType.Private,
    };
    // The `(user, name)` uniqueness constraint spans dashboards, so a blind
    // POST of the reserved name 400s `dashboard_exists` for anyone who has
    // applied a scope before.
    const writeByName = async () => {
      const found = await findReservedScopeSearchId();
      if (found != null) {
        return putSavedSearch({ id: found, savedSearch: payload });
      }
      try {
        return await postSavedSearch(payload);
      } catch (error) {
        // The name can be taken by something this list cannot show -- an
        // analytics dashboard, or a search another tab just created -- so
        // one more lookup decides between a stale miss and a real clash.
        const recovered = await findReservedScopeSearchId();
        if (recovered == null) {
          throw error;
        }
        return putSavedSearch({
          id: recovered,
          savedSearch: payload,
        });
      }
    };

    const write = async () => {
      const pointerId = settings.scope?.savedSearchId;
      if (pointerId == null) {
        return writeByName();
      }
      try {
        return await putSavedSearch({
          id: pointerId,
          savedSearch: payload,
        });
      } catch {
        // The pointer can outlive its search, and re-deriving once is
        // enough: a second miss means the name is genuinely free.
        return writeByName();
      }
    };

    const writeAndToast = async () => {
      try {
        const saved = await write();
        if (saved?.id == null) {
          throw new Error(
            intlService.getIntl().formatMessage(messages.savedSearchError),
          );
        }
        return saved;
      } catch (error) {
        if (error instanceof Error && error.message) {
          showToast({ status: 'danger', message: error.message });
        }
        throw error;
      }
    };

    const saved = await writeAndToast();

    // Saved search first, preference second, so a failed second write
    // leaves the user unscoped rather than scoped-but-unrecorded. The
    // residue is a saved search whose id nobody stored, which the next
    // apply recovers by name.
    const next = {
      ...settings,
      scope: {
        savedSearchId: toPreferenceId(saved.id),
        name: RESERVED_SCOPE_NAME,
        description,
        query,
      },
    };
    await writeHomeScopePreference(next, options);
    return { settings: next };
  });
}
